'use client';

import { useEffect, useRef, useState } from 'react';
import type { backButton as BackButton } from '@telegram-apps/sdk-react';
import { usePathname, useRouter } from 'next/navigation';
import { useNavigation } from '../contexts/NavigationContext';
import { isWikiPath } from '../lib/wikiPath';

interface BackButtonOptions {
  customHandler?: () => void;
  isDisabled?: boolean;
}

declare global {
  interface Window {
    globalCustomHandler: (() => void) | undefined;
    globalIsDisabled: boolean;
  }
}

type BackButtonModule = typeof BackButton;

let backButtonPromise: Promise<BackButtonModule | null> | null = null;
function loadBackButton(): Promise<BackButtonModule | null> {
  if (backButtonPromise) return backButtonPromise;
  if (typeof window === 'undefined') {
    backButtonPromise = Promise.resolve(null);
    return backButtonPromise;
  }
  backButtonPromise = import('@telegram-apps/sdk-react').then((m) => m.backButton);
  return backButtonPromise;
}

export function useTelegramBackButtonSetup() {
  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;
  const router = useRouter();
  const onRouteLayer = isWikiPath(usePathname());
  const routeLayerRef = useRef({ open: onRouteLayer, router });
  routeLayerRef.current = { open: onRouteLayer, router };
  const [bb, setBb] = useState<BackButtonModule | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadBackButton().then((mod) => {
      if (!cancelled) setBb(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!bb) return;
    if (!bb.isSupported()) return;

    if (!bb.isMounted()) {
      try {
        bb.mount();
      } catch (error) {
        console.error('Failed to mount back button:', error);
        return;
      }
    }

    const handleBackClick = () => {
      // WHY: страница вики в Mini App лежит слоем поверх стека — «Назад» листает её историю, а не экраны под ней
      if (routeLayerRef.current.open) {
        routeLayerRef.current.router.back();
        return;
      }
      if (window.globalIsDisabled) return;
      if (window.globalCustomHandler) {
        window.globalCustomHandler();
        return;
      }
      const nav = navRef.current;
      if (nav.canGoBack()) nav.goBack();
    };

    bb.onClick(handleBackClick);
    return () => bb.offClick(handleBackClick);
  }, [bb]);

  useEffect(() => {
    if (!bb) return;
    if (!bb.isSupported()) return;

    if (onRouteLayer || navigation.canGoBack()) {
      bb.show();
    } else {
      bb.hide();
    }
  }, [bb, navigation, navigation.state.stacks, navigation.state.activeTab, onRouteLayer]);
}

export function useTelegramBackButton(options?: BackButtonOptions) {
  const { customHandler, isDisabled = false } = options || {};

  useEffect(() => {
    window.globalCustomHandler = customHandler;
    window.globalIsDisabled = isDisabled;

    return () => {
      window.globalCustomHandler = undefined;
      window.globalIsDisabled = false;
    };
  }, [customHandler, isDisabled]);
}
